import React, { useEffect } from "react";
import { PortalImageCard } from "./portal_image_card";
import styles from "./membership_card_list.module.css";
import { Spacer } from "../../spacer";
import { PortalButton } from "./portal_button";
import { useMediaQuery } from "../../../hooks";
import { usePortalHistory } from "../hooks/use_portal_history";
import { useMembershipSubscriptionQuery } from "../hooks/use_membership_subscription_query";
import { useAnalytics } from "@/lib/analytics/analytics";
import { stylex } from "@/components";
import { colors } from "@/offering_v2/components/colors";
import { formatDate } from "@/offering/date_utils";
import { useUpcomingPaymentQuery } from "../hooks/use_upcoming_payment_query";
import { formatCurrency } from "@/lib/currency/currency";

interface MembershipCardListProps {
  partnerId: string;
}

export function MembershipCardList({ partnerId }: MembershipCardListProps) {
  const analytics = useAnalytics();
  const { subscriptions } = useMembershipSubscriptionQuery(partnerId);

  useEffect(() => {
    analytics.event("View Membership Portal", { Source: "Portal" });
  }, [analytics]);

  return (
    <div className={styles.list}>
      {subscriptions?.map((s) => (
        <div key={s.id} className={styles.cardWrapper}>
          <MembershipCard
            id={s.id}
            title={s.membership.name}
            imgUrl={s.membership.imgUrl}
            isCancelled={s.status === "canceled"}
            endDate={s.endDate}
            partnerId={partnerId}
          />
        </div>
      ))}
    </div>
  );
}

interface MembershipCardProps {
  id: string;
  imgUrl: string;
  title: string;
  isCancelled: boolean;
  endDate?: string | null;
  partnerId: string;
}

function MembershipCard({
  imgUrl,
  title,
  id,
  isCancelled,
  endDate,
  partnerId,
}: MembershipCardProps) {
  const { device } = useMediaQuery();
  const { toMembershipPage } = usePortalHistory();

  const { data: upcomingPaymentData } = useUpcomingPaymentQuery(partnerId, id);

  const detailsContent = (
    <div className="d-flex flex-column justify-content-between h-100">
      <div>
        <div className={styles.title}>{title}</div>
        {upcomingPaymentData && (
          <div className="mt-2">
            Your next bill is for{" "}
            <b>
              {formatCurrency(
                upcomingPaymentData.totalPrice,
                upcomingPaymentData.currency
              )}
            </b>{" "}
            on <b>{formatDate(upcomingPaymentData.paymentDate)}</b>
          </div>
        )}
        {endDate && (
          <div className="mt-2">
            Access to your membership {isCancelled ? "ended" : "ends"} on{" "}
            <b>{formatDate(endDate)}</b>
          </div>
        )}
        {isCancelled && (
          <div
            className="mt-2 px-2 py-1 rounded-5 fw-bold"
            style={stylesX("cancelChip")}
          >
            CANCELLED
          </div>
        )}
      </div>
      <div>
        <Spacer size={32} />
        <PortalButton onClick={() => toMembershipPage(id)}>
          View your membership
        </PortalButton>
      </div>
    </div>
  );

  return (
    <PortalImageCard
      imgUrl={imgUrl}
      imgAlt={title}
      imgHeight={device === "mobile" ? 160 : 240}
      details={detailsContent}
    />
  );
}

const stylesX = stylex.create({
  cancelChip: {
    color: colors.grapefruitCore,
    background: colors.grapefruitMinus80,
    fontSize: "12px",
    width: "fit-content",
    height: "fit-content",
  },
});
